import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { PageWrapper } from '@/layout/components/PageWrapper.tsx';
import { PageHeader } from '@/layout/components/PageHeader.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import { useUser } from '@/hooks/useUser.ts';
import {
  useGetCallQualityDataQuery,
  useCollectCallQualityMutation,
  type CallQualityCall,
  type CallQualityBucketStats,
} from '@/store/callQualityApi.ts';

type SortKey =
  | 'server_name'
  | 'peer'
  | 'call_id'
  | 'source'
  | 'destination'
  | 'duration_seconds'
  | 'recv_lost'
  | 'recv_pct'
  | 'send_lost'
  | 'send_pct';

type SortDirection = 'asc' | 'desc';

function formatDuration(seconds: number | null): string {
  if (seconds === null) {
    return '-';
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
}

function formatPct(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '-';
  }
  return `${value.toFixed(2)}%`;
}

function BucketCard({ title, stats }: { title: string; stats: CallQualityBucketStats | undefined }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{stats?.calls_count ?? 0} שיחות</CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-muted-foreground">% אבדן ממוצע (מקבל)</div>
          <div className="text-lg font-medium">{formatPct(stats?.avg_recv_pct)}</div>
        </div>
        <div>
          <div className="text-muted-foreground">% אבדן ממוצע (שולח)</div>
          <div className="text-lg font-medium">{formatPct(stats?.avg_send_pct)}</div>
        </div>
        <div>
          <div className="text-muted-foreground">מקסימום חבילות אבודות (מקבל)</div>
          <div className="text-lg font-medium">{stats?.max_recv_lost ?? '-'}</div>
        </div>
        <div>
          <div className="text-muted-foreground">מקסימום חבילות אבודות (שולח)</div>
          <div className="text-lg font-medium">{stats?.max_send_lost ?? '-'}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function SortableHeader(props: {
  label: string;
  sortKeyName: SortKey;
  sortKey: SortKey;
  sortDirection: SortDirection;
  onToggle: (key: SortKey) => void;
}) {
  const { label, sortKeyName, sortKey, sortDirection, onToggle } = props;
  const isActive = sortKey === sortKeyName;
  return (
    <th
      className="px-4 py-3 text-right cursor-pointer select-none whitespace-nowrap"
      onClick={() => onToggle(sortKeyName)}
    >
      {label}
      {isActive ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
}

export default function CallQualityPage() {
  const { user } = useUser();
  const isSuperuser = !!user?.is_superuser;

  const [fetchDestination, setFetchDestination] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('recv_pct');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const { data, isFetching, refetch } = useGetCallQualityDataQuery(undefined, { skip: !isSuperuser });
  const [collectCallQuality, { isLoading: isCollecting }] = useCollectCallQualityMutation();

  async function handleCollect() {
    try {
      const result = await collectCallQuality({ fetch_destination: fetchDestination }).unwrap();
      toast.success(result.msg || 'המשימה נשלחה לביצוע');
    } catch {
      toast.error('שגיאה בשליחת בקשת השאיבה');
    }
  }

  async function handleLoadFromRedis() {
    setShowTable(true);
    try {
      await refetch();
    } catch {
      toast.error('שגיאה בטעינת הנתונים');
    }
  }

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  }

  const filteredCalls = useMemo(() => {
    const calls = data?.calls || [];
    const term = search.trim().toLowerCase();
    const filtered = term
      ? calls.filter((c) =>
          [c.server_name, c.peer, c.call_id, c.source, c.destination].some((v) =>
            (v || '').toLowerCase().includes(term),
          ),
        )
      : calls;

    return [...filtered].sort((a, b) => {
      const av = a[sortKey] as CallQualityCall[SortKey];
      const bv = b[sortKey] as CallQualityCall[SortKey];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data?.calls, search, sortKey, sortDirection]);

  if (!isSuperuser) {
    return (
      <PageWrapper>
        <div className="text-center py-20 text-gray-500">
          <p className="text-xl">גישה מוגבלת לסופר יוזר בלבד</p>
        </div>
      </PageWrapper>
    );
  }

  const stats = data?.stats;

  return (
    <PageWrapper>
      <PageHeader title="סטטיסטיקות איכות שיחות" />

      <Card>
        <CardHeader>
          <CardTitle>שאיבת נתונים מהשרתים</CardTitle>
          <CardDescription>
            השאיבה מתבצעת ברקע (Task) ומתחברת לשרתים ב-SSH באופן יזום בלבד. לאחר שליחת הבקשה יש
            להמתין מספר שניות ואז ללחוץ על &quot;טען נתונים מ-Redis&quot; כדי לראות את התוצאה המעודכנת.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="fetch-destination"
              checked={fetchDestination}
              onCheckedChange={(c) => setFetchDestination(Boolean(c))}
            />
            <Label htmlFor="fetch-destination">אתר את יעד השיחה</Label>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button type="button" onClick={handleCollect} disabled={isCollecting}>
              {isCollecting ? 'שולח...' : 'הרץ שאיבת נתונים מהשרתים'}
            </Button>
            <Button type="button" variant="outline" onClick={handleLoadFromRedis} disabled={isFetching}>
              {isFetching ? 'טוען...' : 'טען נתונים מ-Redis'}
            </Button>
          </div>
          <div className="text-sm text-muted-foreground">
            מועד שאיבה אחרון:{' '}
            {data?.fetched_at ? new Date(data.fetched_at).toLocaleString('he-IL') : 'אין נתונים עדיין'}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <BucketCard title="כלל השיחות הפעילות" stats={stats?.buckets.all} />
        <BucketCard title="שיחות מרבע השעה האחרונה" stats={stats?.buckets.last_15_min} />
        <BucketCard title="שיחות מהשעה האחרונה" stats={stats?.buckets.last_hour} />
      </div>

      {!!stats?.server_breakdown?.length && (
        <Card>
          <CardHeader>
            <CardTitle>פילוח לפי שרת</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-3 text-right">שרת</th>
                    <th className="px-4 py-3 text-right">כמות שיחות</th>
                    <th className="px-4 py-3 text-right">% אבדן ממוצע (מקבל)</th>
                    <th className="px-4 py-3 text-right">% אבדן ממוצע (שולח)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {stats.server_breakdown.map((row) => (
                    <tr key={row.server_name} className="hover:bg-gray-50">
                      <td className="px-4 py-3">{row.server_name}</td>
                      <td className="px-4 py-3">{row.calls_count}</td>
                      <td className="px-4 py-3">{formatPct(row.avg_recv_pct)}</td>
                      <td className="px-4 py-3">{formatPct(row.avg_send_pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {showTable && (
        <Card>
          <CardHeader>
            <CardTitle>טבלת שיחות מפורטת</CardTitle>
            <CardDescription>
              {filteredCalls.length} שיחות מתוך {data?.calls.length || 0}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              type="text"
              placeholder="חיפוש לפי שרת / Peer / Call ID / מקור / יעד"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                  <tr>
                    <SortableHeader label="שרת" sortKeyName="server_name" sortKey={sortKey} sortDirection={sortDirection} onToggle={toggleSort} />
                    <SortableHeader label="Peer" sortKeyName="peer" sortKey={sortKey} sortDirection={sortDirection} onToggle={toggleSort} />
                    <SortableHeader label="Call ID" sortKeyName="call_id" sortKey={sortKey} sortDirection={sortDirection} onToggle={toggleSort} />
                    <SortableHeader label="מקור" sortKeyName="source" sortKey={sortKey} sortDirection={sortDirection} onToggle={toggleSort} />
                    <SortableHeader label="יעד" sortKeyName="destination" sortKey={sortKey} sortDirection={sortDirection} onToggle={toggleSort} />
                    <SortableHeader label="משך" sortKeyName="duration_seconds" sortKey={sortKey} sortDirection={sortDirection} onToggle={toggleSort} />
                    <SortableHeader label="אבדן מקבל" sortKeyName="recv_lost" sortKey={sortKey} sortDirection={sortDirection} onToggle={toggleSort} />
                    <SortableHeader label="% אבדן מקבל" sortKeyName="recv_pct" sortKey={sortKey} sortDirection={sortDirection} onToggle={toggleSort} />
                    <SortableHeader label="אבדן שולח" sortKeyName="send_lost" sortKey={sortKey} sortDirection={sortDirection} onToggle={toggleSort} />
                    <SortableHeader label="% אבדן שולח" sortKeyName="send_pct" sortKey={sortKey} sortDirection={sortDirection} onToggle={toggleSort} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCalls.map((call) => (
                    <tr key={`${call.server_id}-${call.call_id}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3">{call.server_name}</td>
                      <td className="px-4 py-3">{call.peer}</td>
                      <td className="px-4 py-3 font-mono text-xs">{call.call_id}</td>
                      <td className="px-4 py-3">{call.source || '-'}</td>
                      <td className="px-4 py-3">{call.destination || '-'}</td>
                      <td className="px-4 py-3">{formatDuration(call.duration_seconds)}</td>
                      <td className="px-4 py-3">{call.recv_lost}</td>
                      <td className="px-4 py-3">{formatPct(call.recv_pct)}</td>
                      <td className="px-4 py-3">{call.send_lost}</td>
                      <td className="px-4 py-3">{formatPct(call.send_pct)}</td>
                    </tr>
                  ))}
                  {!filteredCalls.length && (
                    <tr>
                      <td colSpan={10} className="px-4 py-6 text-center text-muted-foreground">
                        אין שיחות להצגה
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </PageWrapper>
  );
}
